/**
 * EntityImagePicker
 *
 * Picks the image for a catalog record (product type, appointment type,
 * membership, facility). Functionally the corp-dashboard photo-picker-modal,
 * single account only:
 *
 * - Choose: the images already assigned to this record (when it is saved), then
 *   the photo library with a folder filter and search.
 * - Upload: one image file, max 10 MB, with progress.
 *
 * A saved record (`entityId` set) is assigned on the spot through
 * `POST /product-image-urls` and `onSelected` gets the URL afterwards. A record
 * that is not saved yet (`entityId` null) only gets the URL back; the caller
 * assigns it after create with `assignEntityImage`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ImageIcon, Search, Upload, X } from 'lucide-react';
import { Button, Input, Modal, Select, Spinner, Tabs } from '@/components/ui';
import { cn } from '@/lib/utils';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  ENTITY_IMAGE_FOLDERS,
  MAX_ENTITY_IMAGE_BYTES,
  assignEntityImage,
  listAssignedImages,
  listLibraryPhotos,
  listPhotoFolders,
  uploadEntityImage,
  type AssignedEntityImage,
  type EntityImageType,
  type LibraryPhoto,
} from '@/services/entityImages.service';

export { assignEntityImage };
export type { EntityImageType };

export interface EntityImagePickerProps {
  entityType: EntityImageType;
  /** The saved record's id; null (or 0) = not saved yet, so nothing is assigned. */
  entityId: number | null;
  /** Photo folder an upload is filed under. Defaults to the entity's system folder. */
  folder?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the chosen URL — after it is assigned, when `entityId` is set. */
  onSelected: (url: string) => void;
  /** The record's current image; left out of the lists so it is not offered again. */
  currentImageUrl?: string | null;
  title?: string;
  /**
   * 'svg' for a vector-only slot (Branding's SVG Logo): only SVG files upload
   * and only SVG images are offered. Otherwise SVG is refused, as before.
   * SVG uploads are accepted for logos only.
   */
  imageKind?: ImageKind;
}

export type ImageKind = 'raster' | 'svg';

function isSvgUrl(url: string): boolean {
  return /\.svg(\?|#|$)/i.test(url);
}

export const ENTITY_IMAGE_LABELS: Record<EntityImageType, string> = {
  'product-type': 'product type',
  'appointment-type': 'appointment type',
  membership: 'membership',
  facility: 'facility',
  logo: 'logo',
  'account-alert': 'alert',
};

/** Checks a file against the picker's rules; returns the error to show, or null. */
export function validateEntityImageFile(file: File, kind: ImageKind = 'raster'): string | null {
  const svg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  if (kind === 'svg') {
    if (!svg) return 'Choose an SVG file.';
  } else if (!file.type.startsWith('image/') || svg) {
    // The upload endpoint refuses SVG here (422), so it is caught first.
    return 'Choose an image file (PNG, JPG, GIF or WebP).';
  }
  if (file.size > MAX_ENTITY_IMAGE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `This image is ${mb} MB. The maximum is 10 MB.`;
  }
  return null;
}

type TabId = 'choose' | 'upload';

export function EntityImagePicker({
  entityType,
  entityId,
  folder,
  open,
  onOpenChange,
  onSelected,
  currentImageUrl,
  title,
  imageKind = 'raster',
}: EntityImagePickerProps) {
  const label = ENTITY_IMAGE_LABELS[entityType];
  const uploadFolder = folder || ENTITY_IMAGE_FOLDERS[entityType];
  const savedId = entityId != null && entityId > 0 ? entityId : null;

  const [tab, setTab] = useState<TabId>('choose');

  // Choose tab
  const [assigned, setAssigned] = useState<AssignedEntityImage[]>([]);
  const [library, setLibrary] = useState<LibraryPhoto[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [folderFilter, setFolderFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  // Upload tab
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [fileError, setFileError] = useState('');
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Fresh state every time it opens.
  useEffect(() => {
    if (!open) return;
    setTab('choose');
    setAssigned([]);
    setLibrary([]);
    setFolderFilter('');
    setSearch('');
    setDebouncedSearch('');
    setLoadError('');
    setSelectedUrl(null);
    setPendingFile(null);
    setPreviewUrl('');
    setFileError('');
    setProgress(0);
    setDragOver(false);
    setSaving(false);
    setSaveError('');
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Assigned images (saved records only) and the folder list, once per open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    listPhotoFolders()
      .then((f) => {
        if (!cancelled) setFolders(Array.from(new Set(f.map((x) => x.folder).filter(Boolean))));
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      });
    if (savedId) {
      setLoadingAssigned(true);
      listAssignedImages(entityType, savedId)
        .then((a) => {
          if (!cancelled) setAssigned(a);
        })
        .catch(() => {
          if (!cancelled) setLoadError('Could not load images.');
        })
        .finally(() => {
          if (!cancelled) setLoadingAssigned(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, entityType, savedId]);

  // The library, again whenever the folder or the search changes.
  const libraryRequest = useRef(0);
  useEffect(() => {
    if (!open) return;
    const req = ++libraryRequest.current;
    setLoadingLibrary(true);
    listLibraryPhotos({ folder: folderFilter || undefined, search: debouncedSearch || undefined })
      .then((p) => {
        if (req === libraryRequest.current) setLibrary(p);
      })
      .catch(() => {
        if (req === libraryRequest.current) {
          setLibrary([]);
          setLoadError('Could not load images.');
        }
      })
      .finally(() => {
        if (req === libraryRequest.current) setLoadingLibrary(false);
      });
  }, [open, folderFilter, debouncedSearch]);

  const current = currentImageUrl?.trim() || '';
  const q = debouncedSearch.toLowerCase();

  const assignedShown = useMemo(() => {
    const seen = new Set<string>();
    return assigned.filter((img) => {
      const url = img.productUrl?.trim();
      if (!url || url === current || seen.has(url)) return false;
      seen.add(url);
      if (q && !`${img.productTypeName ?? ''} ${url}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [assigned, current, q]);

  const libraryShown = useMemo(() => {
    const skip = new Set<string>(assigned.map((a) => a.productUrl?.trim()).filter(Boolean));
    if (current) skip.add(current);
    return library.filter((p) => {
      const url = p.url?.trim();
      if (!url || skip.has(url)) return false;
      // An SVG slot offers only SVGs; every other slot leaves them out.
      if (isSvgUrl(url) !== (imageKind === 'svg')) return false;
      skip.add(url);
      return true;
    });
  }, [library, assigned, current, imageKind]);

  const close = useCallback(() => {
    if (!saving) onOpenChange(false);
  }, [saving, onOpenChange]);

  /** Assign (saved record) then hand the URL back. */
  const commit = async (url: string) => {
    if (savedId) await assignEntityImage(entityType, savedId, url);
    onSelected(url);
    onOpenChange(false);
  };

  const chooseUrl = async (url = selectedUrl) => {
    if (!url || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await commit(url);
      toast.success(savedId ? 'Image updated.' : 'Image selected.');
    } catch (e) {
      const msg = getErrorMessage(e, 'Could not set the image.');
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const stageFile = (file: File | null | undefined) => {
    setFileError('');
    setSaveError('');
    if (!file) return;
    const err = validateEntityImageFile(file, imageKind);
    if (err) {
      setPendingFile(null);
      setPreviewUrl('');
      setFileError(err);
      return;
    }
    setPendingFile(file);
    setPreviewUrl('');
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearFile = () => {
    setPendingFile(null);
    setPreviewUrl('');
    setFileError('');
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadAndUse = async () => {
    if (!pendingFile || saving) return;
    setSaving(true);
    setSaveError('');
    setProgress(0);
    try {
      const url = await uploadEntityImage(pendingFile, entityType, setProgress, uploadFolder);
      setProgress(100);
      await commit(url);
      toast.success(savedId ? 'Image uploaded and assigned.' : 'Image uploaded.');
    } catch (e) {
      const msg = getErrorMessage(e, 'Upload failed.');
      setSaveError(msg);
      setProgress(0);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const folderOptions = [
    { value: '', label: 'All folders' },
    ...folders.map((f) => ({ value: f, label: f.charAt(0).toUpperCase() + f.slice(1) })),
  ];

  const loading = loadingAssigned || loadingLibrary;

  const chooseContent = (
    <div className="flex min-h-0 flex-1 flex-col pt-4">
      <div className="flex shrink-0 flex-col gap-2 pb-3 sm:flex-row sm:items-center">
        <Input
          aria-label="Search images"
          placeholder="Search images…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftAddon={<Search className="h-4 w-4 text-slate-400" aria-hidden />}
          wrapperClassName="flex-1 min-w-0"
        />
        <Select<string>
          options={folderOptions}
          value={folderFilter}
          onChange={(v) => {
            setFolderFilter(v ?? '');
            setSelectedUrl(null);
          }}
          wrapperClassName="sm:w-48 shrink-0"
        />
      </div>

      {loadError && <p className="pb-2 text-sm text-red-600">{loadError}</p>}

      <div className="min-h-[240px] space-y-4">
        {loading && assignedShown.length === 0 && libraryShown.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : (
          <>
            {savedId != null && assignedShown.length > 0 && (
              <section aria-label={`Assigned to this ${label}`}>
                <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Assigned to this {label}
                </h4>
                <ThumbGrid>
                  {assignedShown.map((img) => (
                    <ImageThumb
                      key={img.productImageID}
                      url={img.productUrl}
                      label={img.productTypeName ?? undefined}
                      selected={selectedUrl === img.productUrl}
                      onSelect={() => setSelectedUrl(img.productUrl)}
                      onUse={() => void chooseUrl(img.productUrl)}
                    />
                  ))}
                </ThumbGrid>
              </section>
            )}

            <section aria-label="Photo library">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                Photo library
              </h4>
              {libraryShown.length > 0 ? (
                <ThumbGrid>
                  {libraryShown.map((p) => (
                    <ImageThumb
                      key={p.id}
                      url={p.url}
                      label={p.altText || p.originalFilename || undefined}
                      selected={selectedUrl === p.url}
                      onSelect={() => setSelectedUrl(p.url)}
                      onUse={() => void chooseUrl(p.url)}
                    />
                  ))}
                </ThumbGrid>
              ) : (
                !loadingLibrary && (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                    <ImageIcon className="mb-1.5 h-8 w-8" aria-hidden />
                    <p className="text-sm">
                      {debouncedSearch ? 'No images match your search.' : 'No library images in this folder.'}
                    </p>
                  </div>
                )
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );

  const uploadContent = (
    <div className="flex min-h-[280px] flex-1 flex-col pt-4">
      <input
        ref={fileInputRef}
        type="file"
        accept={imageKind === 'svg' ? 'image/svg+xml,.svg' : 'image/png,image/jpeg,image/gif,image/webp,image/*'}
        className="hidden"
        data-testid="entity-image-file-input"
        onChange={(e) => stageFile(e.target.files?.[0])}
      />
      {!pendingFile ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Choose an image to upload"
          className={cn(
            'flex flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors',
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
          )}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            stageFile(e.dataTransfer.files?.[0]);
          }}
        >
          <Upload className="mb-3 h-10 w-10 text-slate-400" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Click to select or drag and drop</p>
          <p className="mt-1 text-xs text-slate-400">{imageKind === 'svg' ? 'SVG' : 'PNG, JPG, GIF or WebP'} up to 10 MB</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="relative h-[220px] w-[220px] overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {previewUrl && <img src={previewUrl} alt="Preview" className="h-full w-full object-cover" />}
            {!saving && (
              <button
                type="button"
                aria-label="Remove image"
                onClick={clearFile}
                className="absolute right-2 top-2 rounded-full bg-black/50 p-1 hover:bg-black/70"
              >
                <X className="h-4 w-4 text-white" aria-hidden />
              </button>
            )}
          </div>
          <p className="max-w-[300px] truncate text-sm text-slate-600" title={pendingFile.name}>
            {pendingFile.name}
          </p>
          <p className="text-xs text-slate-400">{(pendingFile.size / 1024).toFixed(0)} KB</p>
          {saving && (
            <div className="w-full max-w-[300px]" aria-label="Upload progress">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full bg-blue-600 transition-[width]"
                  style={{ width: `${progress}%` }}
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <p className="mt-1 text-center text-xs text-slate-500">
                {progress > 0 ? `Uploading… ${progress}%` : 'Uploading…'}
              </p>
            </div>
          )}
        </div>
      )}
      {fileError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {fileError}
        </p>
      )}
    </div>
  );

  const footer = (
    <>
      {saveError && <p className="mr-auto text-sm text-red-600">{saveError}</p>}
      <Button variant="secondary" onClick={close} disabled={saving}>
        Cancel
      </Button>
      {tab === 'choose' ? (
        <Button onClick={() => void chooseUrl()} disabled={!selectedUrl || saving} loading={saving}>
          Use image
        </Button>
      ) : (
        <Button onClick={() => void uploadAndUse()} disabled={!pendingFile || saving} loading={saving}>
          {savedId ? 'Upload and assign' : 'Upload'}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={close}
      title={title ?? `Select ${label} image`}
      size="lg"
      footer={footer}
      closeOnBackdrop={!saving}
    >
      <Tabs
        className="-mx-4 md:-mx-6 -mt-5"
        defaultTab="choose"
        onChange={(id) => {
          setTab(id as TabId);
          setSaveError('');
        }}
        tabs={[
          { id: 'choose', label: 'Choose image', content: <div className="px-4 md:px-6">{chooseContent}</div> },
          { id: 'upload', label: 'Upload new', content: <div className="px-4 md:px-6">{uploadContent}</div> },
        ]}
      />
    </Modal>
  );
}

function ThumbGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2">{children}</div>;
}

function ImageThumb({
  url,
  label,
  selected,
  onSelect,
  onUse,
}: {
  url: string;
  label?: string;
  selected: boolean;
  onSelect: () => void;
  onUse: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={label || 'Image'}
      title={label}
      onClick={onSelect}
      onDoubleClick={onUse}
      className={cn(
        'group relative aspect-square overflow-hidden rounded-lg border-2 transition-all',
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300',
      )}
      style={{ background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #fff 0% 50%) 50%/12px 12px' }}
    >
      <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
      {selected && (
        <span className="absolute right-1 top-1 rounded-full bg-blue-500 p-0.5">
          <Check className="h-3 w-3 text-white" aria-hidden />
        </span>
      )}
      {label && (
        <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="block truncate text-[10px] text-white">{label}</span>
        </span>
      )}
    </button>
  );
}
